import { useEffect, useMemo, useRef, useState } from "react";
import {
  MdCheckCircle,
  MdContentCopy,
  MdPayments,
  MdReceiptLong,
  MdUploadFile,
  MdWarningAmber,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  createManualSubscriptionUpgrade,
  fetchPaymentBankAccounts,
  uploadTransferProof,
} from "../../services/subscriptionService";
import styles from "../../styles/SubscriptionCheckout.module.css";
import {
  clearPendingSubscriptionSelection,
  getPendingSubscriptionSelection,
  isAuthenticated,
} from "../../utils/auth";

const formatPrice = (value, currency = "IDR") => {
  const number = Number(value || 0);

  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(number);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const SubscriptionCheckoutPage = () => {
  const navigate = useNavigate();
  const paymentCardRef = useRef(null);

  const [selection, setSelection] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState("");
  const [payment, setPayment] = useState(null);

  const [loadingSelection, setLoadingSelection] = useState(true);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [transferDate, setTransferDate] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [proofFile, setProofFile] = useState(null);

  const scrollToPaymentCard = () => {
    requestAnimationFrame(() => {
      paymentCardRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      paymentCardRef.current?.focus?.({ preventScroll: true });
    });
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/register", {
        state: {
          next: "/dashboard/subscription/checkout",
        },
        replace: true,
      });
      return;
    }

    const pending = getPendingSubscriptionSelection();

    if (!pending?.planId || !pending?.price?.id) {
      setError("Paket belum dipilih. Silakan pilih paket terlebih dahulu.");
      setLoadingSelection(false);
      return;
    }

    setSelection(pending);
    setSelectedBankAccountId(pending?.bankAccountId || "");
    setTransferAmount(String(pending?.price?.price || ""));
    setLoadingSelection(false);
  }, [navigate]);

  useEffect(() => {
    const loadBanks = async () => {
      try {
        setLoadingBanks(true);

        const data = await fetchPaymentBankAccounts();
        const list = Array.isArray(data) ? data : [];

        setBankAccounts(list);

        const defaultBank = list.find((item) => item.is_default) || list[0];
        if (!selectedBankAccountId && defaultBank?.id) {
          setSelectedBankAccountId(String(defaultBank.id));
        }
      } catch (err) {
        setError(err?.apiMessage || "Gagal memuat rekening tujuan pembayaran.");
      } finally {
        setLoadingBanks(false);
      }
    };

    if (isAuthenticated()) {
      loadBanks();
    }
  }, []);

  useEffect(() => {
    if (error) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [error]);

  const selectedBankAccount = useMemo(() => {
    return bankAccounts.find(
      (item) => String(item.id) === String(selectedBankAccountId)
    );
  }, [bankAccounts, selectedBankAccountId]);

  const handleCreateInvoice = async () => {
    try {
      setCreatingInvoice(true);
      setError("");
      setSuccess("");

      if (!selection?.planId || !selection?.price?.id || !selectedBankAccountId) {
        setError("Paket, harga, dan rekening tujuan wajib dipilih.");
        return;
      }

      const data = await createManualSubscriptionUpgrade({
        subscription_plan_id: selection.planId,
        subscription_plan_price_id: selection.price.id,
        payment_bank_account_id: Number(selectedBankAccountId),
      });

      setPayment(data);
      setSuccess("Invoice berhasil dibuat. Silakan lanjutkan pembayaran.");

      clearPendingSubscriptionSelection();

      await Swal.fire({
        icon: "success",
        title: "Invoice berhasil dibuat",
        text: "Silakan transfer sesuai nominal yang tertera.",
        confirmButtonText: "Lihat Status Pembayaran",
        confirmButtonColor: "#144280",
        allowOutsideClick: false,
        allowEscapeKey: true,
      });

      scrollToPaymentCard();
    } catch (err) {
      const message = err?.apiMessage || "Gagal membuat invoice subscription.";
      setError(message);

      await Swal.fire({
        icon: "error",
        title: "Gagal membuat invoice",
        text: message,
        confirmButtonText: "Tutup",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleUploadProof = async () => {
    try {
      setUploadingProof(true);
      setError("");
      setSuccess("");

      if (!payment?.payment_id) {
        setError("Invoice belum dibuat.");
        return;
      }

      if (!proofFile) {
        setError("Bukti transfer wajib diunggah.");
        return;
      }

      const formData = new FormData();
      formData.append("transfer_date", transferDate);
      formData.append("transfer_amount", transferAmount);
      formData.append("proof_file", proofFile);
      formData.append("notes", notes);

      await uploadTransferProof(payment.payment_id, formData);

      setSuccess("Bukti transfer berhasil diunggah. Menunggu verifikasi admin.");

      setPayment((prev) => ({
        ...prev,
        status: "pending",
        proof_status: "uploaded",
      }));

      await Swal.fire({
        icon: "success",
        title: "Bukti transfer terkirim",
        text: "Tim admin akan memverifikasi pembayaran Anda.",
        confirmButtonText: "OK",
        confirmButtonColor: "#144280",
      });

      scrollToPaymentCard();
    } catch (err) {
      const message = err?.apiMessage || "Gagal mengunggah bukti transfer.";
      setError(message);

      await Swal.fire({
        icon: "error",
        title: "Gagal mengunggah bukti",
        text: message,
        confirmButtonText: "Tutup",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setUploadingProof(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      setSuccess("Rekening berhasil disalin ke clipboard.");
    } catch {
      setError("Gagal menyalin ke clipboard.");
    }
  };

  if (loadingSelection) {
    return (
      <div className={styles.page}>
        <div className={styles.pageShell}>
          <div className={styles.loadingCard}>Memuat checkout...</div>
        </div>
      </div>
    );
  }

  if (error && !selection) {
    return (
      <div className={styles.page}>
        <div className={styles.pageShell}>
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <MdWarningAmber size={28} />
            </div>
            <h1>Checkout tidak tersedia</h1>
            <p>{error}</p>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => navigate("/dashboard/subscription-plans")}
            >
              Kembali pilih paket
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageShell}>
        <header className={styles.header}>
          <div className={styles.headerBadge}>
            <MdPayments size={18} />
            <span>Subscription Checkout</span>
          </div>

          <div className={styles.headerTopRow}>
            <div>
              <h1 className={styles.title}>Checkout Subscription</h1>
              <p className={styles.subtitle}>
                Proses pembayaran menggunakan transfer manual. Setelah invoice
                dibuat, upload bukti transfer untuk verifikasi admin.
              </p>
            </div>

            <div className={styles.stepPill}>
              <MdReceiptLong size={18} />
              <span>Manual Transfer</span>
            </div>
          </div>
        </header>

        {error ? (
          <div className={`${styles.alert} ${styles.alertError}`}>
            <MdWarningAmber size={18} />
            <span>{error}</span>
          </div>
        ) : null}

        {success ? (
          <div className={`${styles.alert} ${styles.alertSuccess}`}>
            <MdCheckCircle size={18} />
            <span>{success}</span>
          </div>
        ) : null}

        <div className={styles.grid}>
          <section className={styles.card}>
            <div className={styles.cardHead}>
              <h2>Ringkasan Paket</h2>
              <span className={styles.cardHeadMeta}>Review sebelum bayar</span>
            </div>

            <div className={styles.summaryRow}>
              <span>Paket</span>
              <strong>{selection?.planName}</strong>
            </div>

            <div className={styles.summaryRow}>
              <span>Harga</span>
              <strong>
                {formatPrice(
                  selection?.price?.price,
                  selection?.price?.currency
                )}
              </strong>
            </div>

            <div className={styles.summaryRow}>
              <span>Durasi</span>
              <strong>{selection?.price?.duration_months || 1} bulan</strong>
            </div>

            <div className={styles.summaryRow}>
              <span>Kuota Listing</span>
              <strong>
                {selection?.plan?.is_unlimited
                  ? "Unlimited"
                  : `${selection?.plan?.property_limit ?? 0} listing`}
              </strong>
            </div>

            <div className={styles.summaryRow}>
              <span>Featured Listing</span>
              <strong>
                {selection?.plan?.is_unlimited
                  ? "Unlimited"
                  : `${selection?.plan?.featured_property_limit ?? 0} item`}
              </strong>
            </div>

            <div className={styles.noticeBox}>
              <MdReceiptLong size={18} />
              <div>
                <strong>Langkah berikutnya</strong>
                <p>
                  Buat invoice, transfer sesuai nominal, lalu upload bukti agar
                  admin dapat memverifikasi pembayaran.
                </p>
              </div>
            </div>
          </section>

          <section className={styles.card}>
            <div className={styles.cardHead}>
              <h2>Pilih Rekening Tujuan</h2>
              <span className={styles.cardHeadMeta}>Rekening aktif</span>
            </div>

            {loadingBanks ? (
              <div className={styles.muted}>Memuat rekening tujuan...</div>
            ) : (
              <div className={styles.bankList}>
                {bankAccounts.map((bank) => (
                  <label
                    key={bank.id}
                    className={`${styles.bankItem} ${
                      String(selectedBankAccountId) === String(bank.id)
                        ? styles.bankItemActive
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="bankAccount"
                      value={bank.id}
                      checked={String(selectedBankAccountId) === String(bank.id)}
                      onChange={() => setSelectedBankAccountId(String(bank.id))}
                    />

                    <div className={styles.bankInfo}>
                      <div className={styles.bankInfoTop}>
                        <strong>
                          {bank.bank_name}
                          {bank.is_default ? " • Default" : ""}
                        </strong>
                        {bank.is_default ? (
                          <span className={styles.defaultBadge}>Default</span>
                        ) : null}
                      </div>
                      <span>{bank.account_name}</span>
                      <span>{bank.account_number}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {selectedBankAccount ? (
              <div className={styles.bankDetail}>
                <div className={styles.summaryRow}>
                  <span>Bank</span>
                  <strong>{selectedBankAccount.bank_name}</strong>
                </div>
                <div className={styles.summaryRow}>
                  <span>Nama Rekening</span>
                  <strong>{selectedBankAccount.account_name}</strong>
                </div>
                <div className={styles.summaryRow}>
                  <span>No Rekening</span>
                  <strong>{selectedBankAccount.account_number}</strong>
                </div>

                <button
                  type="button"
                  className={styles.copyBtn}
                  onClick={() =>
                    handleCopy(
                      `${selectedBankAccount.bank_name} ${selectedBankAccount.account_number} ${selectedBankAccount.account_name}`
                    )
                  }
                >
                  <MdContentCopy size={18} />
                  Salin Rekening
                </button>
              </div>
            ) : null}

            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleCreateInvoice}
              disabled={creatingInvoice || !selectedBankAccountId}
            >
              {creatingInvoice ? "Membuat Invoice..." : "Buat Invoice Transfer Manual"}
            </button>
          </section>

          {payment ? (
            <section
              ref={paymentCardRef}
              tabIndex={-1}
              className={`${styles.card} ${styles.cardFull} ${styles.paymentFocus}`}
            >
              <div className={styles.cardHead}>
                <h2>Status Pembayaran</h2>
                <span className={styles.cardHeadMeta}>
                  {payment.status === "pending"
                    ? "Menunggu pembayaran"
                    : "Berhasil"}
                </span>
              </div>

              <div className={styles.paymentHero}>
                <div className={styles.paymentHeroLeft}>
                  <span className={styles.paymentLabel}>Invoice</span>
                  <strong className={styles.paymentInvoice}>
                    {payment.invoice_no}
                  </strong>
                  <p className={styles.paymentHint}>
                    Total pembayaran:{" "}
                    <strong>{formatPrice(payment.amount, payment.currency)}</strong>
                  </p>
                </div>

                <div className={styles.paymentHeroRight}>
                  <span>Status</span>
                  <strong>{payment.status}</strong>
                </div>
              </div>

              <div className={styles.paymentMetaGrid}>
                <div className={styles.metaCard}>
                  <span>Jatuh Tempo</span>
                  <strong>{formatDateTime(payment.payment_deadline_at)}</strong>
                </div>
                <div className={styles.metaCard}>
                  <span>Metode</span>
                  <strong>Transfer Manual</strong>
                </div>
                <div className={styles.metaCard}>
                  <span>Verifikasi</span>
                  <strong>{payment.proof_status || "none"}</strong>
                </div>
              </div>

              <div className={styles.transferForm}>
                <h3>Upload Bukti Transfer</h3>

                <div className={styles.formGrid}>
                  <div className={styles.field}>
                    <label>Tanggal Transfer</label>
                    <input
                      type="date"
                      className="mp-input"
                      value={transferDate}
                      onChange={(e) => setTransferDate(e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <label>Nominal Transfer</label>
                    <input
                      type="number"
                      className="mp-input"
                      value={transferAmount}
                      onChange={(e) => setTransferAmount(e.target.value)}
                    />
                  </div>

                  <div className={styles.fieldFull}>
                    <label>Bukti Transfer</label>
                    <input
                      type="file"
                      className="mp-input"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                    />
                  </div>

                  <div className={styles.fieldFull}>
                    <label>Catatan</label>
                    <textarea
                      className="mp-input"
                      rows={4}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Tambahkan catatan jika diperlukan"
                    />
                  </div>
                </div>

                <div className={styles.actionRow}>
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={() => navigate("/dashboard/subscription-plans")}
                  >
                    Kembali ke Paket
                  </button>

                  <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={handleUploadProof}
                    disabled={uploadingProof}
                  >
                    <MdUploadFile size={18} />
                    {uploadingProof ? "Mengunggah..." : "Kirim Bukti Transfer"}
                  </button>
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckoutPage;
